import { useState, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Check, Cookie } from 'lucide-react'

export default function CookieBanner() {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  const [animationsPaused, setAnimationsPaused] = useState(false)

  useEffect(() => {
    const seen = localStorage.getItem('apav-privacy-notice')
    if (!seen) {
      const timer = setTimeout(() => setVisible(true), 1200)
      return () => clearTimeout(timer)
    }
  }, [])

  useEffect(() => {
    const checkMotion = () => {
      const isPaused = document.documentElement.classList.contains('a11y-pause-animations')
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      setAnimationsPaused(isPaused || prefersReduced)
    }
    checkMotion()
    const observer = new MutationObserver(checkMotion)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  const handleOk = () => {
    localStorage.setItem('apav-privacy-notice', 'seen')
    setVisible(false)
  }

  const transitionProps = { duration: animationsPaused ? 0 : 0.45, ease: 'easeOut' }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 50, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 50, opacity: 0, scale: 0.95 }}
          transition={transitionProps}
          className="fixed bottom-6 start-6 md:start-auto md:end-6 md:max-w-md z-[1000]"
          role="region"
          aria-label={t('cookieBanner.regionAria')}
        >
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-slate-200 p-6 flex flex-col gap-4 shadow-black/40">
            <div className="flex gap-4 items-start">
              <div className="p-3 bg-brand-50 rounded-2xl text-brand-600 flex-shrink-0">
                <Cookie size={28} />
              </div>
              <div className="flex-grow">
                <h3 className="text-base font-extrabold text-slate-900 mb-1.5">{t('cookieBanner.title')}</h3>
                {/* Texto da frota (igual ao do precisodeajuda). A etiqueta da ligação é
                    <a> e não <link>: `link` é um elemento HTML void e o parser do
                    <Trans> fechava-o sozinho, deixando «Saiba mais» sem ligação. */}
                <p className="text-slate-600 text-xs leading-relaxed">
                  <Trans
                    i18nKey="cookieBanner.body"
                    components={{
                      a: (
                        <a
                          href="https://apav.pt/politica-de-privacidade/politica-de-cookies/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-600 font-extrabold hover:underline"
                        />
                      ),
                    }}
                  />
                </p>
              </div>
            </div>

            <div className="flex justify-end border-t border-slate-100 pt-4">
              <button
                onClick={handleOk}
                className="px-5 py-2.5 rounded-xl bg-brand-600 text-white font-bold hover:bg-brand-700 transition-colors shadow-lg shadow-brand-500/20 text-xs flex items-center justify-center gap-1.5 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
              >
                <Check size={14} />
                {t('cookieBanner.accept')}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
